import {
  ASC,
  DataGridLayout,
  Filters,
  FiltersLayout,
  GetDataMethodType,
  List,
  ListHeader,
  ListPageLayout,
  QuickFilter,
  SearchInput,
} from 'framework/list';
import {
  useEmployeeService,
  ACTIVE_STATUS,
  useEmployeeUrl,
  EMPLOYEE_TYPES_ALIAS,
  EmployeePositionField as PositionField,
  EmployeePositionFilter,
  OfficeFilterById,
} from 'domain/shared/employee';
import { EMPLOYEE_COMPETENCY_LEVEL } from 'domain/shared/performanceMeasurement';
import ViewEmployeeButton from 'domain/people/employee/shared/button/ViewEmployeeButton';
import CreateEmployeeButton from 'domain/people/employee/shared/button/CreateEmployeeButton';
import EmployeeWorkingStatusFilter from 'domain/people/employee/List/filter/EmployeeWorkingStatusFilter';
import { BadgeField, FullDateField, SearchLinkField, SearchResultField, TextField } from 'framework/ui/field';
import { NewBadge } from 'framework/ui/ui-builder';
import EmployeeTypeFilter from 'domain/people/employee/List/filter/EmployeeTypeFilter';
import EmployeeWorkingStatusField from 'domain/people/employee/shared/field/EmployeeWorkingStatusField';
import EmployeeProbationFilter from './filter/EmployeeProbationFilter';
import CompetencyLevelFilter from '../performanceMeasurement/List/filter/CompetencyLevelFilter';
import EmployeeExpertiseFilter from './filter/EmployeeExpertiseFilter';
import useFeatureToggle from 'framework/featureToggle';
import { Features } from 'infrastructure/featureToggle';
import InternBadge from '../shared/badges/InternBadge';
import ContractorBadge from 'domain/shared/employee/badges/ContractorBadge';
import ActiveProbationBadge from '../shared/badges/ActiveProbationBadge';
import { TechnologiesFilter } from 'domain/shared/career';

const EmployeesListPage = () => {
  const { search, checkIsNewEmployee, getFullName } = useEmployeeService();
  const { getSingleEntityUrl } = useEmployeeUrl();
  const { isFeatureEnabled } = useFeatureToggle();

  const quickFilters = [
    <QuickFilter
      key="with-active-probation"
      label="With active probation"
      state={{ 'specification:hasActiveProbation': { eq: true } }}
    />,
  ];

  const filterFields = [
    <EmployeeWorkingStatusFilter key="working-status" source="workingStatus" label="Working status" />,
    <EmployeeTypeFilter key="employee-type" source="employeeType" label="Type" />,
    <EmployeePositionFilter key="position-id" source="position.id" label="Position" />,
    <TechnologiesFilter key="technology-id" source="specializationInfo.technology.id" label="Technology" />,
    <CompetencyLevelFilter
      key="competency-level"
      source="specializationInfo.competencyLevel"
      label="Competency level"
      options={EMPLOYEE_COMPETENCY_LEVEL}
      locationFilterSource="officeId"
    />,
    <EmployeeProbationFilter key="probation" source="specification:hasActiveProbation" label="Probation" />,
    <OfficeFilterById key="office" source="specification:office" label="Current office" />,
    ...(isFeatureEnabled(Features.EXPERTISE)
      ? [<EmployeeExpertiseFilter key="expertise" source="specification:expertise" label="Expertise" />]
      : []),
  ];

  const filters = (
    <Filters>
      <FiltersLayout quickFilters={quickFilters.length > 0 ? quickFilters : []}>{filterFields}</FiltersLayout>
    </Filters>
  );

  const fields = [
    <SearchLinkField key="alias" source="alias" url={getSingleEntityUrl} label="Worker ID" isSortable width={135} />,
    <BadgeField
      key="name"
      badges={[
        <NewBadge
          id="newEmployee"
          isVisible={employee =>
            checkIsNewEmployee(employee?.initializationState) && employee?.employeeType !== EMPLOYEE_TYPES_ALIAS.INTERN
          }
        />,
        <InternBadge id="intern" isVisible={employee => employee?.employeeType === EMPLOYEE_TYPES_ALIAS.INTERN} />,
        <ContractorBadge
          id="contractorEmployee"
          isVisible={employee => employee?.employeeType === EMPLOYEE_TYPES_ALIAS.CONTRACTOR}
        />,
      ]}
      label="Name"
      source="nameEn.lastName"
      isSortable
    >
      <SearchResultField value={employee => getFullName(employee?.nameEn)} />
    </BadgeField>,
    <PositionField key="position" label="Position" source="positionId" isSortable />,
    <TextField key="working-email" source="workingEmail" label="Syberry email" isSortable />,
    <BadgeField
      key="working-status"
      label="Working status"
      source="workingStatus"
      badges={[<ActiveProbationBadge isVisible={employee => employee?.probation?.onProbation} />]}
      isSortable
    >
      <EmployeeWorkingStatusField source="workingStatus" />
    </BadgeField>,
    <TextField
      key="current-location"
      source="currentLocation"
      sortSource="extendedInformation.homeAddress.country"
      label="Current location"
      isSortable
    />,
    <FullDateField key="updated-at" source="updatedAt" label="Last modified" isSortable />,
  ];

  return (
    <List
      getDataMethod={search as GetDataMethodType}
      defaultSortField="nameEn.lastName"
      defaultSortOrder={ASC}
      defaultFilter={{ workingStatus: { in: [ACTIVE_STATUS] } }}
    >
      <ListPageLayout
        header={<ListHeader label="Workers" icon="fa-user" />}
        filter={filters}
        actions={[<CreateEmployeeButton key="create-employee-button" />]}
        search={<SearchInput label="worker name or ID" />}
      >
        <DataGridLayout
          buttons={[<ViewEmployeeButton key="view-employee-button" source="alias" />]}
          rowKeySource="alias"
        >
          {fields}
        </DataGridLayout>
      </ListPageLayout>
    </List>
  );
};

export default EmployeesListPage;
